import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Loader2, Trash2, PlayCircle, AlertCircle, CalendarClock, Clock, Calendar, Mail, Edit } from 'lucide-react';
import Card from './Card';
import Modal from './Modal';
import ScheduleQueryModal from './QueryComponent/ScheduleQueryModal';

const ScheduledReportsManagement = () => {
  const { currentUser, isAdmin } = useAuth();
  const [scheduledQueries, setScheduledQueries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [queryToDelete, setQueryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [runningQueries, setRunningQueries] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [queryToEdit, setQueryToEdit] = useState(null);

  useEffect(() => {
    fetchScheduledQueries();
  }, [currentUser]);

  const fetchScheduledQueries = async () => {
    if (!currentUser?.email || !isAdmin) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const getScheduledQueries = httpsCallable(functions, 'getScheduledQueries');
      const result = await getScheduledQueries({
        userEmail: currentUser.email  // ← Change from "adminEmail" to "userEmail"
      });

      if (result.data.success) {
        console.log('Raw scheduled queries data:', result.data.scheduledQueries);
        setScheduledQueries(result.data.scheduledQueries);
      } else {
        throw new Error(result.data.error || 'Failed to fetch scheduled reports');
      }
    } catch (error) {
      console.error('Error fetching scheduled queries:', error);
      setError(error.message || 'Failed to load scheduled reports');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (query) => {
    setQueryToDelete(query);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!queryToDelete?.id || !currentUser?.email) {
      setError('Invalid operation');
      return;
    }
  
    try {
      setIsDeleting(true);
      
      // Directly make a function call to delete the schedule
      const manageScheduledQuery = httpsCallable(functions, 'manageScheduledQuery');
      
      // Logging clear parameters for debugging
      const params = {
        userEmail: currentUser.email,  // ← Change from "adminEmail" to "userEmail"
        action: 'delete',
        scheduledQueryId: queryToDelete.id
      };
      
      console.log('Deleting scheduled query with params:', params);
      
      const result = await manageScheduledQuery(params);

      console.log('Delete result:', result);

      if (result.data && result.data.success) {
        // Remove the deleted query from the local state (don't wait for refresh)
        setScheduledQueries(prev => prev.filter(q => q.id !== queryToDelete.id));
        setIsDeleteModalOpen(false);
        setQueryToDelete(null);
        setSaveStatus({
          type: 'success',
          message: 'Scheduled report deleted successfully'
        });
      } else {
        throw new Error((result.data && result.data.error) || 'Failed to delete scheduled report');
      }
    } catch (error) {
      console.error('Error deleting scheduled query:', error);
      setError(error.message || 'Failed to delete scheduled report');
      setSaveStatus({
        type: 'error',
        message: error.message || 'Failed to delete scheduled report'
      });
    } finally {
      setIsDeleting(false);
      // Always close the modal to prevent UI lock
      setTimeout(() => {
        setIsDeleteModalOpen(false);
        setQueryToDelete(null);
        setTimeout(() => setSaveStatus(null), 3000);
      }, 500);
    }
  };

  const handleEditClick = (query) => {
    setQueryToEdit(query);
    setIsEditModalOpen(true);
  };

  const handleRunNow = async (queryId) => {
    if (!currentUser?.email || !isAdmin) return;
  
    try {
      setRunningQueries(prev => ({ ...prev, [queryId]: true }));
      const triggerScheduledQuery = httpsCallable(functions, 'triggerScheduledQuery');
      
      console.log('Running scheduled query with params:', {
        userEmail: currentUser.email,  // ← Change from "adminEmail" for consistency
        scheduledQueryId: queryId
      });
      
      const result = await triggerScheduledQuery({
        userEmail: currentUser.email,  // ← This was already correct
        scheduledQueryId: queryId
      });

      console.log('Run result:', result);

      if (result.data && result.data.success) {
        // Refresh the queries to get updated lastRun and nextRun times
        await fetchScheduledQueries();
        setSaveStatus({
          type: 'success',
          message: 'Report generated and sent successfully'
        });
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        throw new Error((result.data && result.data.error) || 'Failed to run scheduled report');
      }
    } catch (error) {
      console.error('Error running scheduled query:', error);
      setError(error.message || 'Failed to run scheduled report');
      setSaveStatus({
        type: 'error',
        message: error.message || 'Failed to run scheduled report'
      });
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setRunningQueries(prev => ({ ...prev, [queryId]: false }));
    }
  };

  const formatSchedule = (schedule) => {
    if (!schedule) return 'Unknown';

    const frequency = schedule.frequency;
    switch (frequency) {
      case 'hourly':
        return `Every hour at ${schedule.minute} minutes`;
      case 'daily':
        return `Daily at ${String(schedule.hour).padStart(2, '0')}:${String(schedule.minute).padStart(2, '0')}`;
      case 'weekly':
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const day = days[schedule.dayOfWeek];
        return `Weekly on ${day} at ${String(schedule.hour).padStart(2, '0')}:${String(schedule.minute).padStart(2, '0')}`;
      case 'monthly':
        return `Monthly on day ${schedule.dayOfMonth} at ${String(schedule.hour).padStart(2, '0')}:${String(schedule.minute).padStart(2, '0')}`;
      default:
        return 'Custom schedule';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>;
      case 'disabled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Disabled</span>;
      case 'error':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Error</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const formatDateTime = (date) => {
    if (!date) return 'Never';
    
    // Check if it's a Firestore timestamp object (standard format)
    if (date && typeof date === 'object' && date.seconds !== undefined) {
      // Convert Firestore timestamp to Date
      return new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000).toLocaleString();
    }
    
    // Check if it's a Firestore timestamp object (serialized format with underscore prefix)
    if (date && typeof date === 'object' && date._seconds !== undefined) {
      // Convert serialized Firestore timestamp to Date
      return new Date(date._seconds * 1000 + (date._nanoseconds || 0) / 1000000).toLocaleString();
    }
    
    // If it's already a Date object or a date string
    try {
      return new Date(date).toLocaleString();
    } catch (e) {
      console.error('Error formatting date:', e, date);
      return 'Invalid date';
    }
  };

  // If user isn't an admin, don't render the component
  if (!isAdmin) return null;

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center mb-2">
        <div className="flex-1">
          <h2 className="text-card-title-xl text-text">Scheduled Reports</h2>
        </div>
        <button
          onClick={fetchScheduledQueries}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:opacity-50 bg-white border border-gray-200 hover:bg-gray-50 h-9 px-4 text-text shadow-sm"
        >
          Refresh
        </button>
      </div>
  
      {error && (
        <div className="bg-red-50 text-red-500 p-3 rounded-md flex items-center text-sm border border-red-100">
          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
  
      {saveStatus && (
        <div className={`${
          saveStatus.type === 'success' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-red-50 text-red-500 border-red-100'
        } p-3 rounded-md flex items-center text-sm border`}>
          {saveStatus.type === 'success' ? (
            <svg className="h-4 w-4 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          )}
          <span>{saveStatus.message}</span>
        </div>
      )}
  
      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : scheduledQueries.length === 0 ? (
        <div className="bg-gray-50 p-6 rounded-lg text-center border border-gray-100">
          <CalendarClock className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-text mb-2">No Scheduled Reports</h3>
          <p className="text-sm text-text-light max-w-md mx-auto">
            No scheduled reports found. You can schedule reports from any saved query in the dashboard.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {scheduledQueries.map((query) => (
            <div 
              key={query.id}
              className={`bg-white rounded-lg border ${!query.enabled ? 'opacity-75 border-gray-100' : 'border-gray-200'} overflow-hidden shadow-sm transition-all hover:border-gray-300`}
            >
              <div className="p-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2.5">
                      <h3 className="text-base font-semibold text-text">
                        {query.name}
                      </h3>
                      {getStatusBadge(query.status)}
                    </div>
                    
                    <div className="flex items-center mt-1.5">
                      <CalendarClock className="h-3.5 w-3.5 text-text-lighter mr-1.5" />
                      <p className="text-xs text-text-light">
                        {formatSchedule(query.schedule)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleRunNow(query.id)}
                      disabled={!query.enabled || runningQueries[query.id]}
                      className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:opacity-50 bg-primary text-white hover:bg-primary/90 h-8 px-3"
                      title="Run Now"
                    >
                      {runningQueries[query.id] ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <>
                          <PlayCircle className="h-3.5 w-3.5 mr-1" />
                          <span>Run Now</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleEditClick(query)}
                      disabled={runningQueries[query.id]}
                      className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:opacity-50 bg-white border border-gray-200 text-text-light hover:bg-gray-50 h-8 w-8"
                      title="Edit"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(query)}
                      disabled={runningQueries[query.id]}
                      className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:opacity-50 bg-white border border-gray-200 text-red-500 hover:bg-red-50 hover:border-red-100 h-8 w-8"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div className="flex items-start bg-gray-50 p-2 rounded-md">
                    <Calendar className="h-3.5 w-3.5 text-text-lighter mt-0.5 mr-1.5" />
                    <div>
                      <span className="font-medium text-text block">Next Run:</span>
                      <span className="text-text-light">{formatDateTime(query.nextRun)}</span>
                    </div>
                  </div>
                  <div className="flex items-start bg-gray-50 p-2 rounded-md">
                    <Clock className="h-3.5 w-3.5 text-text-lighter mt-0.5 mr-1.5" />
                    <div>
                      <span className="font-medium text-text block">Last Run:</span>
                      <span className="text-text-light">{formatDateTime(query.lastRun)}</span>
                    </div>
                  </div>
                  <div className="flex items-start bg-gray-50 p-2 rounded-md">
                    <Mail className="h-3.5 w-3.5 text-text-lighter mt-0.5 mr-1.5" />
                    <div>
                      <span className="font-medium text-text block">Recipients:</span>
                      <span className="text-text-light">
                        {query.recipients?.length
                          ? (query.recipients.length === 1 
                              ? query.recipients[0] 
                              : `${query.recipients.length} emails`)
                          : "None"}
                      </span>
                    </div>
                  </div>
                </div>
                
                {query.errorDetails && (
                  <div className="mt-3 flex items-start bg-red-50 p-2 rounded-md text-xs border border-red-100">
                    <AlertCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 mr-1.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-red-600 block">Error:</span>
                      <span className="text-red-500">{query.errorDetails}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
  
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isDeleting) {
            setIsDeleteModalOpen(false);
            setQueryToDelete(null);
          }
        }}
        title="Delete Scheduled Report"
        onSubmit={handleDeleteConfirm}
        isLoading={isDeleting}
        confirmText="Delete"
        confirmStyle="danger"
      >
        <div className="space-y-4">
          <p>Are you sure you want to delete this scheduled report?</p>
          {queryToDelete && (
            <div className="bg-gray-50 p-3 rounded-md border border-gray-100">
              <p className="font-medium text-text">{queryToDelete.name}</p>
              <p className="text-xs text-text-light mt-1">{formatSchedule(queryToDelete.schedule)}</p>
              <p className="text-xs text-text-lighter mt-2">Report ID: {queryToDelete.id}</p>
            </div>
          )}
          <p className="text-sm text-red-600">This action cannot be undone.</p>
        </div>
      </Modal>
  
      {/* Edit Modal - Using ScheduleQueryModal */}
      {queryToEdit && (
        <ScheduleQueryModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setQueryToEdit(null);
            // Refresh the list after closing to show any updates
            fetchScheduledQueries();
          }}
          savedQueryId={queryToEdit.savedQueryId}
          scheduledQueryId={queryToEdit.id}
          initialData={queryToEdit}
        />
      )}
    </div>
  );
};

export default ScheduledReportsManagement;